import {
  Container,
  GlProgram,
  Mesh,
  MeshGeometry,
  Shader,
  Texture,
  UniformGroup,
  type RenderOptions,
  type RenderTexture,
  type TextureSource,
  type UNIFORM_TYPES,
  type WebGLRenderer,
} from 'pixi.js';
import type { UniformValue, VideoFrameSource } from '../core/definitions';
import type { Logger } from '../core/services';
import { glUniformName, parseUniforms } from './shaderWrap';

export const QUAD_VERTEX = `#version 300 es
precision highp float;
in vec2 aPosition;
in vec2 aUV;
out vec2 vUv;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vUv = aUV;
}
`;

export function createQuadGeometry(): MeshGeometry {
  return new MeshGeometry({
    positions: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
    uvs: new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]),
    indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
  });
}

/**
 * Compiled-program cache keyed by the complete fragment source. Every fragment
 * is test-compiled and linked on the raw context first, so a broken shader is
 * reported once and never reaches the renderer's draw path.
 */
export class ShaderPrograms {
  private readonly cache = new Map<string, GlProgram | null>();

  constructor(
    private readonly renderer: WebGLRenderer,
    private readonly logger: Logger,
  ) {}

  /** Returns null when the fragment does not compile. */
  get(fragment: string, label: string, vertex: string = QUAD_VERTEX): GlProgram | null {
    const key = vertex === QUAD_VERTEX ? fragment : `${vertex}\u0000${fragment}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const program = new GlProgram({ vertex, fragment, name: `studio-${label}` });
    const error = this.verify(program);
    if (error !== null) {
      this.logger.log('error', 'render', `Shader "${label}" failed to compile and is skipped`, error);
      this.cache.set(key, null);
      return null;
    }
    this.cache.set(key, program);
    return program;
  }

  /** Forget verification results (the context was restored; programs must be relinked). */
  reset(): void {
    this.cache.clear();
  }

  private verify(program: GlProgram): string | null {
    const gl = this.renderer.gl;
    if (gl.isContextLost()) return 'WebGL context lost';
    const vs = gl.createShader(gl.VERTEX_SHADER);
    const fs = gl.createShader(gl.FRAGMENT_SHADER);
    const linked = gl.createProgram();
    if (!vs || !fs || !linked) return 'could not allocate shader objects';
    let error: string | null = null;
    gl.shaderSource(vs, program.vertex!);
    gl.compileShader(vs);
    gl.shaderSource(fs, program.fragment!);
    gl.compileShader(fs);
    if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
      error = gl.getShaderInfoLog(fs) || 'fragment shader compile error';
    } else if (!gl.getShaderParameter(vs, gl.COMPILE_STATUS)) {
      error = gl.getShaderInfoLog(vs) || 'vertex shader compile error';
    } else {
      gl.attachShader(linked, vs);
      gl.attachShader(linked, fs);
      gl.linkProgram(linked);
      if (!gl.getProgramParameter(linked, gl.LINK_STATUS)) error = gl.getProgramInfoLog(linked) || 'program link error';
    }
    gl.deleteProgram(linked);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    return error;
  }
}

interface TypeInfo {
  pixi: UNIFORM_TYPES;
  components: number;
  integer: boolean;
}

const TYPES: Record<string, TypeInfo> = {
  float: { pixi: 'f32', components: 1, integer: false },
  int: { pixi: 'i32', components: 1, integer: true },
  bool: { pixi: 'i32', components: 1, integer: true },
  uint: { pixi: 'u32', components: 1, integer: true },
  vec2: { pixi: 'vec2<f32>', components: 2, integer: false },
  vec3: { pixi: 'vec3<f32>', components: 3, integer: false },
  vec4: { pixi: 'vec4<f32>', components: 4, integer: false },
  ivec2: { pixi: 'vec2<i32>', components: 2, integer: true },
  ivec3: { pixi: 'vec3<i32>', components: 3, integer: true },
  ivec4: { pixi: 'vec4<i32>', components: 4, integer: true },
  mat2: { pixi: 'mat2x2<f32>', components: 4, integer: false },
  mat3: { pixi: 'mat3x3<f32>', components: 9, integer: false },
  mat4: { pixi: 'mat4x4<f32>', components: 16, integer: false },
};

interface UserUniform {
  /** Name in the definition's `uniforms` record. */
  name: string;
  /** Name in the linked program. */
  glName: string;
  kind: 'scalar' | 'array' | 'sampler';
}

export type TextureResolver = (resource: VideoFrameSource, version: number) => TextureSource | null;

type UniformStructure = Record<string, { value: unknown; type: UNIFORM_TYPES; size?: number }>;

/**
 * One drawable instance of a wrapped fragment: the shared program plus this
 * instance's uniform values and sampler bindings. The resource layout is
 * derived from the fragment text alone, so every instance of a program has the
 * same layout (PixiJS caches its sync code per program).
 */
export class PassShader {
  readonly shader: Shader;
  private readonly values: Record<string, unknown>;
  private readonly samplers: Record<string, TextureSource>;
  private readonly user: UserUniform[] = [];

  constructor(
    program: GlProgram,
    userFragment: string,
    builtinNames: ReadonlySet<string>,
    builtinSamplers: readonly string[],
    builtinUniforms: UniformStructure,
  ) {
    const structure: UniformStructure = { ...builtinUniforms };
    const resources: Record<string, unknown> = {};
    for (const name of builtinSamplers) resources[name] = Texture.EMPTY.source;
    for (const decl of parseUniforms(userFragment)) {
      if (builtinNames.has(decl.name)) continue;
      const glName = glUniformName(decl.name);
      if (decl.type === 'sampler2D') {
        resources[glName] = Texture.EMPTY.source;
        this.user.push({ name: decl.name, glName, kind: 'sampler' });
        continue;
      }
      const info = TYPES[decl.type];
      if (!info) continue;
      const count = info.components * decl.size;
      if (count === 1) {
        structure[glName] = { value: 0, type: info.pixi };
        this.user.push({ name: decl.name, glName, kind: 'scalar' });
      } else {
        const value = info.integer ? new Int32Array(count) : new Float32Array(count);
        structure[glName] = { value, type: info.pixi, size: decl.size };
        this.user.push({ name: decl.name, glName, kind: 'array' });
      }
    }
    const group = new UniformGroup(structure);
    resources.uStudioPass = group;
    this.shader = new Shader({ glProgram: program, resources });
    this.values = group.uniforms as Record<string, unknown>;
    this.samplers = this.shader.resources as Record<string, TextureSource>;
  }

  setFloat(glName: string, value: number): void {
    this.values[glName] = value;
  }

  setVec2(glName: string, x: number, y: number): void {
    const target = this.values[glName] as Float32Array;
    target[0] = x;
    target[1] = y;
  }

  setSampler(glName: string, source: TextureSource | null): void {
    this.samplers[glName] = source ?? Texture.EMPTY.source;
  }

  /** Copies definition-supplied uniform values; missing ones keep their previous value. */
  applyUser(uniforms: Record<string, UniformValue> | undefined, resolve: TextureResolver): void {
    if (!uniforms) return;
    for (let i = 0; i < this.user.length; i++) {
      const entry = this.user[i]!;
      const value = uniforms[entry.name];
      if (value === undefined) continue;
      if (entry.kind === 'sampler') {
        const texture = typeof value === 'object' && value !== null && 'texture' in value ? value : null;
        this.setSampler(entry.glName, texture?.texture ? resolve(texture.texture, texture.version ?? 0) : null);
      } else if (entry.kind === 'scalar') {
        this.values[entry.glName] = typeof value === 'number' ? value : value === true ? 1 : 0;
      } else if (typeof value === 'object' && value !== null && 'length' in value) {
        const target = this.values[entry.glName] as Float32Array | Int32Array;
        const count = Math.min(target.length, value.length);
        for (let k = 0; k < count; k++) target[k] = value[k]!;
      }
    }
  }

  destroy(): void {
    this.shader.destroy();
  }
}

const TRANSPARENT: [number, number, number, number] = [0, 0, 0, 0];
const OPAQUE_BLACK: [number, number, number, number] = [0, 0, 0, 1];

/** Draws one full-target quad with a PassShader into a render texture or onto the canvas. */
export class PassRunner {
  private readonly root = new Container();
  private readonly geometry = createQuadGeometry();
  private mesh: Mesh<MeshGeometry, Shader> | null = null;
  private readonly options: RenderOptions = { container: this.root };

  constructor(private readonly renderer: WebGLRenderer) {}

  /** `target` null draws to the canvas over opaque black; a render texture is cleared to transparent. */
  draw(pass: PassShader, target: RenderTexture | null, width: number, height: number): void {
    if (!this.mesh) {
      this.mesh = new Mesh({ geometry: this.geometry, shader: pass.shader, texture: Texture.WHITE });
      this.root.addChild(this.mesh);
    } else {
      this.mesh.shader = pass.shader;
    }
    this.mesh.scale.set(width, height);
    const options = this.options;
    options.container = this.root;
    options.target = target ?? undefined;
    options.transform = undefined;
    options.clear = true;
    options.clearColor = target ? TRANSPARENT : OPAQUE_BLACK;
    this.renderer.render(options);
  }

  destroy(): void {
    this.root.destroy({ children: true });
    this.geometry.destroy();
  }
}
